import { useEffect, useMemo, useState } from 'react';

import { statesArray } from '../constants/states';
import { ttJurisdictionDefaultConditions } from '../constants/taterTitle';
import {
  StateAbbreviation,
  TitleRegistrationOption,
  TtDocumentName,
  TtProductType,
  useGetTtJurisdictionLazyQuery,
} from '../gql/generated/graphql';
import { DealWithFarthestState } from '../pages/dashboard/utils';
import { DocumentType, documents } from '../pages/documents/components/ThingsWeNeed/utils';
import { getRequiredDocTypes } from '../pages/documents/utils';
import { getAddressValue } from '../utils/address';

type UseJurisdictionDocsOptions = {
  skip?: boolean;
};

type UseJurisdictionDocsResult = {
  requiredDocs: DocumentType[];
  loading: boolean;
};

export const useJurisdictionDocs = (
  deal?: DealWithFarthestState,
  { skip = false }: UseJurisdictionDocsOptions = {},
): UseJurisdictionDocsResult => {
  const [registrationDocs, setRegistrationDocs] = useState<TtDocumentName[]>([]);
  const [titleDocs, setTitleDocs] = useState<TtDocumentName[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [ttJurisdictionQuery, { loading: queryLoading }] = useGetTtJurisdictionLazyQuery();

  const customer = deal?.customer;
  const stateAbbr = customer?.address?.state?.toUpperCase();
  const driversLicenseStateAbbr = customer?.drivers_license_address?.state?.toUpperCase();
  const customerState = statesArray.find((entry) => entry.value === stateAbbr)?.name ?? '';

  const driversLicenseAddress = getAddressValue({
    address_line: customer?.drivers_license_address?.address_line,
    address_line_2: customer?.drivers_license_address?.address_line_2,
    city: customer?.drivers_license_address?.city,
    state: customer?.drivers_license_address?.state,
    zip: customer?.drivers_license_address?.zip,
  });
  const currentAddress = getAddressValue({
    address_line: customer?.address?.address_line,
    address_line_2: customer?.address?.address_line_2,
    city: customer?.address?.city,
    state: customer?.address?.state,
    zip: customer?.address?.zip,
  });

  useEffect(() => {
    if (skip || !stateAbbr) return;

    const fetchData = async () => {
      const { data } = await ttJurisdictionQuery({
        variables: {
          state: stateAbbr as StateAbbreviation,
          types: [TtProductType.Registration, TtProductType.Title],
          conditions: {
            ...ttJurisdictionDefaultConditions(stateAbbr as StateAbbreviation),
            // Non-core lienholders are persisted with the Other slug, which maps to
            // TaterTitle's Other.
            lienholder: deal?.car?.payoff?.lienholder_slug,
            hasCobuyer: !!deal?.cobuyer?.first_name,
            driversLicenseAddress,
            currentAddress,
            vehicleAge: new Date().getFullYear() - parseInt(deal?.car?.year || '0', 10),
            transferringPlates: !!deal?.financial_info?.plate_transfer,
            driversLicenseState: driversLicenseStateAbbr || stateAbbr,
            movedStateDuringLease: !!customer?.address?.moved_states,
            jurisdictionState: stateAbbr,
          },
        },
      });

      setHasFetched(true);

      if (data?.jurisdiction == null) return;

      const reg =
        data.jurisdiction.products?.items?.find((item) => item?.type === TtProductType.Registration)
          ?.documents?.items ?? [];
      const title =
        data.jurisdiction.products?.items?.find((item) => item?.type === TtProductType.Title)
          ?.documents?.items ?? [];

      setRegistrationDocs((reg?.map((item) => item?.type?.name) || []) as TtDocumentName[]);
      setTitleDocs((title?.map((item) => item?.type?.name) || []) as TtDocumentName[]);
    };
    fetchData();
  }, [
    skip,
    currentAddress,
    driversLicenseAddress,
    stateAbbr,
    driversLicenseStateAbbr,
    customer?.address?.moved_states,
    deal?.financial_info?.plate_transfer,
    deal?.car?.payoff?.lienholder_slug,
    deal?.cobuyer?.first_name,
    deal?.car?.year,
  ]);

  const neededDocsTypes = useMemo(
    () =>
      getRequiredDocTypes(
        deal?.financial_info?.title_registration_option === TitleRegistrationOption.TitleOnly,
        titleDocs,
        registrationDocs,
      ),
    [deal?.financial_info?.title_registration_option, titleDocs, registrationDocs],
  );

  const requiredDocs = useMemo(() => {
    if (!deal || !customerState || neededDocsTypes.length === 0) return [];
    const backOfDLRequired = neededDocsTypes.includes(TtDocumentName.BackOfDriversLicense);
    const backOfCobuyerDLRequired = neededDocsTypes.includes(
      TtDocumentName.CobuyerBackOfDriversLicense,
    );

    return documents({
      backOfDLRequired,
      backOfCobuyerDLRequired,
      state: customerState,
      movedStates: deal.customer?.address?.moved_states ?? false,
      hasCobuyer: !!deal.cobuyer?.first_name,
    }).filter((doc) => !doc.neededNow && neededDocsTypes.includes(doc.ttDocumentNameEnumType));
  }, [deal, customerState, neededDocsTypes]);

  const loading = !skip && !!stateAbbr && (queryLoading || !hasFetched);

  return { requiredDocs, loading };
};
